import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Download, FileText, Printer, TrendingUp } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackReport = {
  filters: { period: "month", channel: "all" },
  metrics: {
    revenue: 82430,
    subtotal: 76520,
    tax: 5940,
    shipping: 300,
    channelFees: 3812,
    itemCost: 47442,
    profit: 25266,
    orders: 214,
    refunds: 684,
    marginPercent: 33,
    forecastRevenue: 90200,
  },
  reports: [
    { report: "Sales Summary", period: "month", channel: "All", amount: 82430, status: "Ready" },
    { report: "Tax Liability", period: "month", channel: "All", amount: 5940, status: "Ready" },
    { report: "Marketplace Fees", period: "month", channel: "Facebook market place / eBay", amount: 3812, status: "Ready" },
    { report: "Refund Exposure", period: "month", channel: "All", amount: 684, status: "Review" },
  ],
  channelBreakdown: [
    { channel: "facebook_marketplace", label: "Facebook market place", orders: 142, revenue: 18420, tax: 1204 },
    { channel: "ebay", label: "eBay", orders: 96, revenue: 12880, tax: 842 },
    { channel: "bobbinusa", label: "Bobbin USA", orders: 218, revenue: 28600, tax: 2190 },
  ],
  regionBreakdown: [
    { region: "CA", orders: 48, revenue: 12400, tax: 1054 },
    { region: "NJ", orders: 31, revenue: 8900, tax: 593 },
    { region: "Canada", orders: 22, revenue: 6100, tax: 0 },
  ],
  salesByDay: [
    { date: "2026-05-10", orders: 12, revenue: 4200, tax: 312 },
    { date: "2026-05-11", orders: 15, revenue: 5100, tax: 396 },
    { date: "2026-05-12", orders: 17, revenue: 6200, tax: 442 },
  ],
  channelStats: [],
};

function money(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function percent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function csvEscape(value) {
  const text = String(value ?? "");
  return `"${text.replaceAll('"', '""')}"`;
}

function reportStatusTone(status) {
  if (status === "Ready") return "good";
  if (status === "Review") return "warn";
  return "info";
}

export default function Tax() {
  const [period, setPeriod] = useState("month");
  const [channel, setChannel] = useState("all");
  const { dismiss, toast, toasts } = useToast();

  const loadReports = useCallback(async () => {
    return api.get(`/admin/reports?period=${period}&channel=${channel}`);
  }, [channel, period]);

  const { data, error, loading, reload } = useAdminResource(loadReports, fallbackReport);
  const reportData = data || fallbackReport;

  useEffect(() => {
    if (error && !loading) toast(`Using preview reports: ${error}`, "warning");
  }, [error, loading, toast]);

  const maxChannelRevenue = useMemo(() => {
    return Math.max(1, ...reportData.channelBreakdown.map((item) => Number(item.revenue || 0)));
  }, [reportData.channelBreakdown]);

  const maxDailyRevenue = useMemo(() => {
    return Math.max(1, ...reportData.salesByDay.map((item) => Number(item.revenue || 0)));
  }, [reportData.salesByDay]);
  const reportPagination = usePagination(reportData.reports, [period, channel], 10);
  const regionPagination = usePagination(reportData.regionBreakdown, [period, channel], 10);

  const exportCsv = () => {
    const rows = [
      ["Report", "Period", "Channel", "Amount", "Status"],
      ...reportData.reports.map((item) => [item.report, item.period, item.channel, Number(item.amount || 0).toFixed(2), item.status]),
      [],
      ["Channel", "Orders", "Revenue", "Tax"],
      ...reportData.channelBreakdown.map((item) => [item.label, item.orders, Number(item.revenue || 0).toFixed(2), Number(item.tax || 0).toFixed(2)]),
      [],
      ["Region", "Orders", "Revenue", "Tax"],
      ...reportData.regionBreakdown.map((item) => [item.region, item.orders, Number(item.revenue || 0).toFixed(2), Number(item.tax || 0).toFixed(2)]),
    ];
    const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tax-report-${period}-${channel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast("CSV report downloaded", "success");
  };

  const printReport = () => {
    const win = window.open("", "_blank", "width=1100,height=800");
    if (!win) {
      toast("Allow popups to print the report", "error");
      return;
    }

    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Tax and Revenue Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #111827; }
            h1 { margin: 0; font-size: 28px; }
            .muted { color: #6b7280; font-size: 13px; }
            .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 28px 0; }
            .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; }
            .label { color: #6b7280; font-size: 11px; text-transform: uppercase; font-weight: 700; }
            .value { margin-top: 8px; font-size: 22px; font-weight: 900; color: #2563eb; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { text-align: left; background: #f3f4f6; font-size: 11px; text-transform: uppercase; padding: 10px; }
            td { border-bottom: 1px solid #e5e7eb; padding: 10px; font-size: 13px; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <h1>Tax and Revenue Report</h1>
          <p class="muted">Period: ${period} / Channel: ${channel} / Generated ${formatUsDateTime(new Date())}</p>
          <div class="grid">
            <div class="card"><div class="label">Revenue</div><div class="value">${money(reportData.metrics.revenue)}</div></div>
            <div class="card"><div class="label">Tax Collected</div><div class="value">${money(reportData.metrics.tax)}</div></div>
            <div class="card"><div class="label">Profit</div><div class="value">${money(reportData.metrics.profit)}</div></div>
            <div class="card"><div class="label">Margin</div><div class="value">${percent(reportData.metrics.marginPercent)}</div></div>
          </div>
          <table>
            <thead><tr><th>Report</th><th>Period</th><th>Channel</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              ${reportData.reports.map((item) => `
                <tr>
                  <td>${item.report}</td>
                  <td>${item.period}</td>
                  <td>${item.channel}</td>
                  <td>${money(item.amount)}</td>
                  <td>${item.status}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          <script>window.print();</script>
        </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Tax & Reports</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Revenue, tax, profit, and channel reports</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            Generate accounting-ready reports from paid orders, marketplace channels, refunds, shipping, and product cost data.
          </p>
        </div>
        <WorkflowToolbar
          actions={[
            { label: "Refresh", onClick: reload },
            { label: "Export CSV", onClick: exportCsv },
            { label: "Print PDF", primary: true, onClick: printReport },
          ]}
        />
      </div>

      <section className="grid gap-4 rounded-2xl bg-white p-4 shadow-sm md:grid-cols-[1fr_1fr_auto] md:items-end">
        <label>
          <span className="mb-2 block text-xs font-black uppercase text-gray-500">Period</span>
          <select className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setPeriod(event.target.value)} value={period}>
            <option value="day">Daily</option>
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
          </select>
        </label>
        <label>
          <span className="mb-2 block text-xs font-black uppercase text-gray-500">Channel</span>
          <select className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setChannel(event.target.value)} value={channel}>
            <option value="all">All Channels</option>
            <option value="facebook_marketplace">Facebook market place</option>
            <option value="ebay">eBay</option>
            <option value="walmart">Walmart</option>
            <option value="bobbinusa">Bobbin USA</option>
            <option value="direct">Direct</option>
          </select>
        </label>
        <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" onClick={reload} type="button">
          <BarChart3 size={16} />
          Generate
        </button>
      </section>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InsightCard title="Revenue" value={money(reportData.metrics.revenue)} note={`${reportData.metrics.orders || 0} paid orders`} tone="money" />
        <InsightCard title="Tax Collected" value={money(reportData.metrics.tax)} note="Estimated liability by order tax" tone="info" />
        <InsightCard title="Estimated Profit" value={money(reportData.metrics.profit)} note={`${percent(reportData.metrics.marginPercent)} gross margin`} tone="good" />
        <InsightCard title="Forecast" value={money(reportData.metrics.forecastRevenue)} note="Projected from current run rate" tone="warn" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-black text-gray-900">Revenue Reports</h2>
              <p className="mt-1 text-sm font-semibold text-gray-500">Sales, tax, fees, refunds, and margin outputs.</p>
            </div>
            <div className="flex gap-2">
              <button className="rounded-xl border border-gray-200 p-3 text-gray-600 hover:text-[#4391d1]" onClick={exportCsv} title="Export CSV" type="button">
                <Download size={18} />
              </button>
              <button className="rounded-xl border border-gray-200 p-3 text-gray-600 hover:text-[#4391d1]" onClick={printReport} title="Print PDF" type="button">
                <Printer size={18} />
              </button>
            </div>
          </div>
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
              <thead>
                <tr className="text-xs font-black uppercase text-gray-400">
                  {["Report", "Period", "Channel", "Amount", "Status"].map((column) => (
                    <th className="px-3 py-3" key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reportPagination.paginatedItems.map((item) => (
                  <tr key={item.report}>
                    <td className="px-3 py-4 font-black text-gray-900">{item.report}</td>
                    <td className="px-3 py-4 font-semibold capitalize text-gray-600">{item.period}</td>
                    <td className="px-3 py-4 font-semibold text-gray-600">{item.channel}</td>
                    <td className="px-3 py-4 font-black text-gray-800">{money(item.amount)}</td>
                    <td className="px-3 py-4"><StatusPill tone={reportStatusTone(item.status)}>{item.status}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls pagination={reportPagination} />
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <TrendingUp className="text-[#4391d1]" />
            <h2 className="text-xl font-black text-gray-900">Profit Detail</h2>
          </div>
          <div className="mt-5 grid gap-3">
            {[
              ["Subtotal", reportData.metrics.subtotal],
              ["Item cost", reportData.metrics.itemCost],
              ["Shipping collected", reportData.metrics.shipping],
              ["Channel fees", reportData.metrics.channelFees],
              ["Refund exposure", reportData.metrics.refunds],
              ["Estimated profit", reportData.metrics.profit],
            ].map(([label, value]) => (
              <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3" key={label}>
                <span className="text-sm font-bold text-gray-500">{label}</span>
                <span className="text-sm font-black text-gray-900">{money(value)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-gray-900">Channel Comparison</h2>
          <div className="mt-5 grid gap-4">
            {reportData.channelBreakdown.length ? reportData.channelBreakdown.map((item) => (
              <div key={item.channel}>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-black text-gray-900">{item.label}</span>
                  <span className="font-bold text-gray-500">{money(item.revenue)} / {item.orders} orders</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-[#4391d1]" style={{ width: `${Math.max(6, (Number(item.revenue || 0) / maxChannelRevenue) * 100)}%` }} />
                </div>
              </div>
            )) : (
              <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-500">No paid channel revenue for this filter.</p>
            )}
          </div>
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-gray-900">Regional Tax Summary</h2>
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
              <thead>
                <tr className="text-xs font-black uppercase text-gray-400">
                  {["Region", "Orders", "Revenue", "Tax"].map((column) => <th className="px-3 py-3" key={column}>{column}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {regionPagination.paginatedItems.map((item) => (
                  <tr key={item.region}>
                    <td className="px-3 py-4 font-black text-gray-900">{item.region}</td>
                    <td className="px-3 py-4 font-semibold text-gray-600">{item.orders}</td>
                    <td className="px-3 py-4 font-semibold text-gray-600">{money(item.revenue)}</td>
                    <td className="px-3 py-4 font-black text-gray-800">{money(item.tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls pagination={regionPagination} />
        </section>
      </div>

      <section className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <FileText className="text-[#4391d1]" />
          <h2 className="text-xl font-black text-gray-900">Sales Trend</h2>
        </div>
        <div className="mt-5 flex h-56 items-end gap-3 overflow-x-auto rounded-xl bg-gray-50 p-4">
          {reportData.salesByDay.length ? reportData.salesByDay.map((item) => (
            <div className="flex min-w-20 flex-1 flex-col items-center justify-end gap-2" key={item.date}>
              <div
                className="w-full rounded-t-lg bg-[#4391d1]"
                style={{ height: `${Math.max(8, (Number(item.revenue || 0) / maxDailyRevenue) * 170)}px` }}
                title={`${item.date}: ${money(item.revenue)}`}
              />
              <span className="text-xs font-black text-gray-500">{item.date.slice(5)}</span>
            </div>
          )) : (
            <p className="self-center text-sm font-bold text-gray-500">No paid order trend data for this filter.</p>
          )}
        </div>
      </section>
    </div>
  );
}
